<template>
  <div>
    <v-skeleton-loader type="table" v-if="loading"></v-skeleton-loader>
    <v-sheet v-else>
      <h2 class="title">
        Location Restrictions
      </h2>
      <v-divider class="mb-4"></v-divider>
      
      <!-- Description -->
      <v-alert
        type="info"
        text
        dense
        class="mb-4"
      >
        Configure which locations to exclude from inventory calculations.
        This allows you to focus on specific locations that matter to your business.
      </v-alert>

      <!-- Single Location Warning -->
      <v-alert
        v-if="!canConfigureRestrictions && availableLocations.length === 1"
        type="error"
        text
        dense
        class="mb-4"
      >
        {{ singleLocationMessage }}
      </v-alert>

      <!-- Empty State (No locations loaded yet) -->
      <v-alert
        v-else-if="!canConfigureRestrictions && availableLocations.length === 0 && !locationsLoading"
        type="error"
        text
        class="mb-4"
      >
        No locations found. Please ensure your store has locations configured.
      </v-alert>

      <v-form ref="form" v-if="canConfigureRestrictions">
        <!-- Restriction Mode -->
        <h3 class="subtitle-1 mb-2">Restriction Mode</h3>
        <v-radio-group
          v-model="locationRestriction.restrictionMode"
          row
          class="mb-4"
          @change="onSettingsChange"
        >
          <v-radio
            label="Ignore selected locations"
            value="ignore"
          ></v-radio>
          <v-radio
            label="Include only selected locations"
            value="include_only"
          ></v-radio>
        </v-radio-group>

        <!-- Mode Description -->
        <v-alert
          :type="locationRestriction.restrictionMode === 'ignore' ? 'info' : 'success'"
          text
          dense
          class="mb-4"
        >
          <template v-if="locationRestriction.restrictionMode === 'ignore'">
            Selected locations will be <strong>excluded</strong> from inventory calculations and reports.
          </template>
          <template v-else>
            Only selected locations will be <strong>included</strong> in inventory calculations and reports.
          </template>
        </v-alert>

        <!-- Location Selection -->
        <h3 class="subtitle-1 mb-2">Select Locations</h3>
        <v-select
          v-model="locationRestriction.restrictedLocations"
          :items="availableLocations"
          item-text="name"
          item-value="id"
          label="Choose locations"
          multiple
          outlined
          chips
          deletable-chips
          dense
          class="mb-4"
          :loading="locationsLoading"
          @change="onSettingsChange"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              small
              :color="locationRestriction.restrictionMode === 'ignore' ? 'warning' : 'success'"
              text-color="white"
              close
              @click:close="removeLocation(item.id)"
            >
              {{ item.name }}
            </v-chip>
          </template>
        </v-select>

        <!-- Applied Contexts -->
        <h3 class="subtitle-1 mb-2">Apply Restrictions To</h3>
        <v-checkbox
          :input-value="true"
          label="Inventory Calculations"
          dense
          class="mb-4"
          disabled
          readonly
        ></v-checkbox>
        <p class="caption grey--text">
          Location restrictions are currently applied only to inventory calculations. Additional options may be available in future releases.
        </p>

        <!-- Validation Error Alert -->
        <v-alert
          v-if="!isValidConfiguration"
          type="error"
          outlined
          dense
          class="mb-4"
        >
          {{ validationErrorMessage }}
        </v-alert>

        <!-- Unsaved Changes Alert -->
        <v-alert
          v-if="hasUnsavedChanges && isValidConfiguration"
          type="info"
          outlined
          dense
          class="mb-4"
        >
          You have unsaved changes. Click "Save Settings" to save your changes or "Discard Changes" to revert.
        </v-alert>

        <!-- Action Buttons -->
        <v-row>
          <v-col>
            <v-btn
              color="primary"
              :disabled="!canSave"
              :loading="saving"
              @click="saveSettings"
            >
              Save Settings
            </v-btn>
            <v-btn
              text
              class="ml-2"
              :disabled="!hasUnsavedChanges"
              @click="discardChanges"
            >
              Discard Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <!-- Success Message -->
      <v-snackbar
        v-model="showSuccessMessage"
        color="success"
        timeout="3000"
      >
        Location restrictions updated successfully!
      </v-snackbar>

      <!-- Error Message -->
      <v-snackbar
        v-model="showErrorMessage"
        color="error"
        timeout="5000"
      >
        {{ errorMessage }}
      </v-snackbar>
    </v-sheet>
  </div>
</template>

<script>
import { LocationRestriction } from '@/models/location_restriction'
import { get } from '@/services/http'

export default {
  name: 'LocationRestrictionSettings',
  
  data() {
    return {
      loading: true,
      locationsLoading: false,
      saving: false,
      showSuccessMessage: false,
      showErrorMessage: false,
      errorMessage: '',
      locationRestriction: new LocationRestriction(),
      originalRestriction: new LocationRestriction(),
      availableLocations: [],
      hasUnsavedChanges: false
    }
  },

  computed: {
    appliedContexts() {
      return this.locationRestriction.getAppliedContexts()
    },

    canSave() {
      return this.hasUnsavedChanges && !this.saving && !this.loading && this.canConfigureRestrictions && this.isValidConfiguration
    },

    canConfigureRestrictions() {
      return this.availableLocations.length > 1
    },

    singleLocationMessage() {
      if (this.availableLocations.length === 1) {
        return `You currently have only one location (${this.availableLocations[0]?.name}). Location restrictions require multiple locations to be configured.`
      }
      return ''
    },

    isValidConfiguration() {
      const selectedCount = this.locationRestriction.restrictedLocations.length
      const totalCount = this.availableLocations.length

      // If ignoring locations, can't ignore ALL of them (need at least one for inventory)
      if (this.locationRestriction.restrictionMode === 'ignore') {
        return selectedCount < totalCount
      }

      // If including only, must include at least ONE location
      if (this.locationRestriction.restrictionMode === 'include_only') {
        return selectedCount > 0
      }

      return true
    },

    validationErrorMessage() {
      if (!this.isValidConfiguration) {
        const selectedCount = this.locationRestriction.restrictedLocations.length
        const totalCount = this.availableLocations.length

        if (this.locationRestriction.restrictionMode === 'ignore' && selectedCount === totalCount) {
          return 'You cannot ignore all locations. At least one location must be available for inventory calculations.'
        }

        if (this.locationRestriction.restrictionMode === 'include_only' && selectedCount === 0) {
          return 'You must include at least one location for inventory calculations.'
        }
      }
      return ''
    }
  },

  async mounted() {
    await this.loadSettings()
    await this.loadLocations()
    this.loading = false
    
    // Add beforeunload warning for unsaved changes
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },

  methods: {
    async loadSettings() {
      try {
        await this.locationRestriction.load()
        // Ensure inventory_calculation is always true
        this.locationRestriction.applyTo.inventory_calculation = true
        // Store original settings for comparison
        this.originalRestriction = this.locationRestriction.clone()
        this.hasUnsavedChanges = false
      } catch (error) {
        console.error('Error loading location restrictions:', error)
        this.showError('Failed to load location restriction settings')
      }
    },

    async loadLocations() {
      try {
        this.locationsLoading = true
        // Fetch real location data from Shopify via dimensions API
        const response = await get('/dimensions/by_location')
        if (response.data && Array.isArray(response.data)) {
          this.availableLocations = response.data.map(location => ({
            id: parseInt(location.id, 10),
            name: location.name || `Location ${location.id}`
          }))
        }
      } catch (error) {
        console.error('Error loading locations:', error)
        // Fallback to empty array if API fails
        this.availableLocations = []
        this.showError('Failed to load store locations. Please refresh the page to try again.')
      } finally {
        this.locationsLoading = false
      }
    },

    async saveSettings() {
      // Validate that we have multiple locations before saving
      if (!this.canConfigureRestrictions) {
        this.showError('Cannot configure location restrictions with only one location')
        return
      }

      // Validate that at least one location will have inventory data
      if (!this.isValidConfiguration) {
        this.showError(this.validationErrorMessage)
        return
      }

      try {
        this.saving = true
        // Ensure inventory_calculation is always true before saving
        this.locationRestriction.applyTo.inventory_calculation = true
        await this.locationRestriction.save()
        // Update original settings after successful save
        this.originalRestriction = this.locationRestriction.clone()
        this.hasUnsavedChanges = false
        this.showSuccessMessage = true
      } catch (error) {
        console.error('Error saving location restrictions:', error)
        this.showError('Failed to save location restriction settings')
      } finally {
        this.saving = false
      }
    },

    onSettingsChange() {
      // Check if current settings differ from original
      this.hasUnsavedChanges = !this.areSettingsEqual(this.locationRestriction, this.originalRestriction)
    },

    areSettingsEqual(current, original) {
      return JSON.stringify(current.toApiFormat()) === JSON.stringify(original.toApiFormat())
    },

    removeLocation(locationId) {
      this.locationRestriction.removeLocationLocal(locationId)
      this.onSettingsChange()
    },

    resetSettings() {
      this.locationRestriction = this.originalRestriction.clone()
      this.hasUnsavedChanges = false
    },

    discardChanges() {
      this.resetSettings()
    },

    getLocationName(locationId) {
      const location = this.availableLocations.find(loc => loc.id === locationId)
      return location ? location.name : `Location ${locationId}`
    },

    showError(message) {
      this.errorMessage = message
      this.showErrorMessage = true
    },

    handleBeforeUnload(event) {
      if (this.hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return event.returnValue
      }
    }
  }
}
</script>

<style scoped>
.title {
  font-weight: 500;
  margin-bottom: 16px;
}

.subtitle-1 {
  font-weight: 500;
}

.v-chip {
  margin: 2px;
}
</style>